import './DesignerInspector.css';

import React, { useEffect, useState } from 'react'
import { Alert, Badge, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap'
import IconButton from '../common/IconButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import ServicePropertiesForm from './ServicePropertiesForm';
import { useDispatch, useSelector } from 'react-redux';
import { updateUsedItem } from '../../slices/designerSlice';
import ApplicationForm from './ApplicationForm';
import { Link } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { setInspectingItem, updateItemLabel } from '../../slices/inspectorSlice';
import { getConnectionDetails, createQueryDetails } from '../../utils';
import { VizClient } from '../../api/VizClient';
import { useToasts } from '../../contexts/toasts';
import AppLoader from '../common/AppLoader';
import { memo } from 'react';
import UserItem from '../common/UserItem';
import { BdaApplication } from '../../models/BdaApplication';
import { RootState } from '../../store';
import { useReactFlow } from 'reactflow';
import { ApplicationMode } from '../list-items/BdaApplicationItem';
import { Tooltip } from 'antd';


type Props = {
  onClose: VoidFunction;
  onApplicationSubmit: (application: BdaApplication) => void;
  onInspectionCancel: React.MouseEventHandler<HTMLButtonElement>;
}

function DesignerInspector({ onClose, onApplicationSubmit, onInspectionCancel }: Props) {
  console.log("[Inspector] re-render");
  const availableStorages = useSelector((state: RootState) => state.designer.availableStorages);
  const inspectingItem = useSelector((state: RootState) => state.inspector.inspectingItem);

  const { setNodes } = useReactFlow();
  const dispatch = useDispatch();

  const [datasetPreview, setDatasetPreview] = useState<any[]>();
  const [show, setShow] = useState(false);
  const { showToast } = useToasts();

  const [serviceLabel, setServiceLabel] = useState<string>();

  useEffect(() => {
    if (inspectingItem) {
      setServiceLabel(inspectingItem.label || inspectingItem.name);
      dispatch(updateUsedItem(inspectingItem));
    }
    else setServiceLabel(undefined);
    // eslint-disable-next-line
  }, [inspectingItem]);

  const handleStorageChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    const value = evt.target.value;
    const found = inspectingItem.service.outputs.findIndex((output: any) => output.id === inspectingItem.type);
    if (found < 0) return;

    const newItem = cloneDeep(inspectingItem);
    newItem.service.outputs[found].targetStorage = +value;
    dispatch(setInspectingItem(newItem));
  }

  const handleCascadeColumnsChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    const found = inspectingItem.service.inputs.findIndex((input: any) => input.id === inspectingItem.type);
    if (found < 0) return;

    const newItem = cloneDeep(inspectingItem);
    newItem.service.inputs[found].dataset.columns = value;
    dispatch(setInspectingItem(newItem));
  }

  const handleClose = () => {
    setShow(false);
    setDatasetPreview(undefined);
  };

  const openDatasetPreview = () => {
    setShow(true);

    const connectionDetails = getConnectionDetails(inspectingItem.datasource, undefined, inspectingItem.datasetFileType);
    const body = createQueryDetails(connectionDetails);
    VizClient.getDatasetPreview(body, connectionDetails.type)
      .then(
        ({ data: preview }) => setDatasetPreview(preview?.result?.data || []),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `Dataset Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handleSelectedChange = (index: number) => (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.checked;
    const found = inspectingItem.service.inputs.findIndex((input: any) => input.id === inspectingItem.type);
    if (found < 0) return;

    const input = inspectingItem.service.inputs[found];
    const newColumns = cloneDeep(input.dataset.columns);
    newColumns[index].selected = value;

    const newItem = cloneDeep(inspectingItem);
    newItem.service.inputs[found].dataset.columns = newColumns;
    dispatch(setInspectingItem(newItem));
  };

  const handleServiceLabelChange = (evt: React.ChangeEvent<HTMLInputElement>) => setServiceLabel(evt.target.value);

  const handleServiceLabelUpdate = () => {
    dispatch(updateItemLabel(serviceLabel));
    setNodes(nodes => {
      const newNodes = [...nodes];
      const found = nodes.findIndex(n => n.id === inspectingItem.ref);
      newNodes[found].data.label = serviceLabel || inspectingItem.name;
      return newNodes;
    });
  }

  const renderPreview = () => {
    if (!datasetPreview) return (
      <div className="d-flex justify-content-center py-3">
        <AppLoader size={250} />
      </div>
    )

    return datasetPreview.length > 0
      ? (
        <div className="table-wrapper">
          <Table className="mb-0" bordered>
            <thead>
              <tr>
                {Object.keys(datasetPreview[0]).map(key => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datasetPreview.map((row, i) => (
                <tr key={i}>
                  {Object.keys(row).map((col, j) => (
                    <td key={j}>{row[col]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )
      : (
        <Alert className="p-2 mb-0">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">Dataset is empty.</span>
        </Alert>
      )
  }

  const renderDatasetDetail = () => {
    return (
      <div className="p-2">
        <Table borderless>
          <thead>
            <tr>
              <th style={{ width: "40%" }}></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="col">Type</th>
              <td className="text-end">{inspectingItem.datasetFileType.type}</td>
            </tr>
            {inspectingItem.datasource && (
              <tr>
                <th scope="col">Data Source</th>
                <td className="text-end">
                  <Link to={`/datasources/${inspectingItem.datasource.id}`} target="_blank" rel="noopener noreferrer">{inspectingItem.datasource.name}</Link>
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {inspectingItem.columns?.length > 0 && (
          <>
            <h6 className="mb-1 text-muted">Dataset Columns</h6>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="text-center">Type</th>
                </tr>
              </thead>
              <tbody>
                {
                  inspectingItem.columns.map((col: any) => (
                    <tr key={col.name}>
                      <td>{col.name}</td>
                      <td className="text-center text-muted">
                        <small>{col.type}</small>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </Table>
          </>
        )}

        {inspectingItem.datasetFileType.type === "table" && (
          <div className="text-center">
            <Button variant="light" size="sm" className="border px-4" onClick={openDatasetPreview}>
              <FontAwesomeIcon icon={solid('eye')} />
              <span className="ms-3">Show Preview</span>
            </Button>
          </div>
        )}

        <Modal size="lg" show={show} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Dataset Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body>{renderPreview()}</Modal.Body>
          <Modal.Footer>
            <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          </Modal.Footer>
        </Modal>
      </div>
    )
  }

  const renderModelDetail = () => {
    return (
      <table className="table-transparent">
        <tbody>
          <tr>
            <th scope="col">Algorithm</th>
            <td>{inspectingItem.algorithmName}</td>
          </tr>
          {inspectingItem.inputs && (
            <tr>
              <th scope="col">Input Columns</th>
              <td>
                <div className="d-flex flex-wrap">
                  {inspectingItem.inputs.split(",").map((col: string) => (
                    <Badge key={col} bg="light" className="text-muted me-2 border">{col}</Badge>
                  ))}
                </div>
              </td>
            </tr>
          )}
          {inspectingItem.trainingDataset && (
            <tr>
              <th scope="col">Dataset</th>
              <td>
                <Link className="d-inline-block" to={`/datasets/${inspectingItem.trainingDataset.id}`} target="_blank" rel="noopener noreferer">
                  <div className="border rounded px-2 bg-light">
                    <FontAwesomeIcon icon={solid('database')} />
                    <span className="ms-2">{inspectingItem.trainingDataset.name}</span>
                  </div>
                </Link>
              </td>
            </tr>
          )}
          <tr>
            <th scope="col">Application</th>
            <td>
              <Link className="d-inline-block" to={`/bda-applications/${inspectingItem.createdByBdaId}`} target="_blank" rel="noopener noreferer">
                <div className="border rounded px-2 bg-light">
                  <FontAwesomeIcon icon={solid('laptop-code')} />
                  <span className="ms-2">{inspectingItem.createdByBdaId}</span>
                </div>
              </Link>
            </td>
          </tr>
          {inspectingItem.datasource && (
            <tr>
              <th scope="col">Data Source</th>
              <td className="text-end">
                <Link to={`/datasources/${inspectingItem.datasource.id}`} target="_blank" rel="noopener noreferrer">{inspectingItem.datasource.name}</Link>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  }

  const selectAll = (allSelected: boolean) => () => {
    const found = inspectingItem.service.inputs.findIndex((input: any) => input.id === inspectingItem.type);
    if (found < 0) return;

    const input = inspectingItem.service.inputs[found];
    const newColumns = cloneDeep(input.dataset.columns);
    newColumns.filter((col: any) => !col.disabled).forEach((col: any) => col.selected = !allSelected);

    const newItem = cloneDeep(inspectingItem);
    newItem.service.inputs[found].dataset.columns = newColumns;
    dispatch(setInspectingItem(newItem));
  }

  const renderAssetSettings = () => {
    if (inspectingItem.type.includes('output-dataset') || inspectingItem.type.includes('output-model')) {
      const targetStorage = inspectingItem.service?.outputs.find((output: any) => output.id === inspectingItem.type)?.targetStorage;
      return (
        <div className="p-2">
          <Form.Group controlId="output-storage">
            <Form.Label>Output Storage</Form.Label>
            <Form.Select size="sm" value={targetStorage || ""} onChange={handleStorageChange}>
              <option value="" hidden disabled>-- select storage --</option>
              {availableStorages?.map(datasource => (
                <option key={datasource.id} value={datasource.id}>{datasource.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      )
    }

    if (inspectingItem.type.includes('input-dataset')) {
      const input = inspectingItem.service.inputs.find((input: any) => input.id === inspectingItem.type);
      const dataset = input?.dataset;

      if (inspectingItem.cascade) return (
        <div className="p-2">
          <Form.Group controlId="cascade-input-columns">
            <Form.Label>Input columns</Form.Label>
            <Form.Control size="sm" placeholder="Service input columns" value={dataset?.columns} onChange={handleCascadeColumnsChange} />
            <Form.Text>Comma-separated names for the columns to feed the algorithm with. Use <u>*</u> to select all the columns.</Form.Text>
          </Form.Group>
        </div>
      )

      const allSelected = dataset?.columns.filter((col: any) => col.selected).length === dataset?.columns.filter((col: any) => !col.disabled).length;
      const acceptedTypes = inspectingItem.service.inputs.find((input: any) => input.id === inspectingItem.type)?.acceptedTypes;

      return (
        <div className="p-2">
          {input && (
            <table className="table-transparent">
              {input.datasetMode && (
                <tr>
                  <th scope="col">Input Mode</th>
                  <td className="text-end">
                    <ApplicationMode mode={input.datasetMode.toUpperCase()} />
                  </td>
                </tr>
              )}
              {input.datasetType && (
                <tr>
                  <th scope="col">Dataset Type</th>
                  <td className="text-end">
                    <ApplicationMode mode={input.datasetType} />
                  </td>
                </tr>
              )}
            </table>
          )}

          <Alert variant="info" className="font-smaller py-2 mb-2">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">Service <b>{inspectingItem.service.name}</b> expects the following value types:</span>
            <ul className="mb-0">
              {acceptedTypes.map((type: string) => (
                <li key={type} className="fw-bold">{type}</li>
              ))}
            </ul>
          </Alert>

          {dataset !== undefined
            ? (
              <Table bordered>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th className="text-center">Type</th>
                    <th className="text-center">
                      <Form.Check type="checkbox" checked={allSelected} onChange={selectAll(allSelected)} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {
                    (dataset.columns as any[]).map((col, index) => (
                      <tr key={col.name}>
                        <td>{col.name}</td>
                        <td className="text-center text-muted">
                          <small>{col.type}</small>
                        </td>
                        <td className="text-center">
                          <Form.Check type="checkbox" checked={col.selected || false} disabled={col.disabled} onChange={handleSelectedChange(index)} />
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </Table>
            )
            : (
              <Alert variant="warning" className="py-2">
                <FontAwesomeIcon icon={solid('exclamation-circle')} />
                <span className="ms-2">No dataset assigned to this input yet.</span>
              </Alert>
            )}
        </div>
      )
    }
  }

  const renderElementDetail = () => {
    switch (inspectingItem.type) {
      default:
        return renderAssetSettings();

      case 'dataset':
        return renderDatasetDetail();

      case 'batch':
      case 'stream':
        return (
          <>
            <Form.Group className="mx-2 mb-3" controlId="label">
              <Form.Label>Label</Form.Label>
              <Form.Control placeholder="Service Label" value={serviceLabel || ""} onChange={handleServiceLabelChange} onBlur={handleServiceLabelUpdate} size="sm" />
            </Form.Group>

            <div className="d-flex align-items-center justify-content-between px-2">
              <Form.Label>Version</Form.Label>
              <Badge bg="light" className="border text-dark">
                <FontAwesomeIcon icon={solid('tag')} />
                <span className="ms-2">{inspectingItem.version}</span>
              </Badge>
            </div>

            <UserItem className="sm border p-1 rounded m-2" user={inspectingItem.owner} organization={inspectingItem.organization} />

            <ServicePropertiesForm properties={inspectingItem.properties} />
          </>
        );

      case 'model':
        return renderModelDetail();
    }
  }

  return (
    <div className="designer-inspector">
      <Row className="gx-0 inspector-header">
        {inspectingItem
          ? (
            <>
              <IconButton className="p-1 px-2 w-auto" onClick={onInspectionCancel} tooltip="Back">
                <FontAwesomeIcon icon={solid('chevron-left')} />
              </IconButton>
              <Col className="overflow-hidden">
                <h6 className="text-uppercase text-truncate mb-0">{inspectingItem.name}</h6>
                {inspectingItem.description && (
                  <Tooltip title={inspectingItem.description} placement="left">
                    <p className="text-alida-secondary font-smaller line-clamp-3 mb-0">{inspectingItem.description}</p>
                  </Tooltip>
                )}
              </Col>
            </>
          )
          : (
            <Col className="overflow-hidden">
              <h6 className="text-uppercase text-truncate mb-0">Create Application</h6>
              <p className="text-alida-secondary mb-0">Fill in application info.</p>
            </Col>
          )
        }
        <IconButton className="p-1 px-2 w-auto" onClick={onClose}>
          <FontAwesomeIcon icon={solid('times')} />
        </IconButton>
      </Row>

      <div className="inspector-body">
        {inspectingItem
          ? renderElementDetail()
          : <ApplicationForm onApplicationSubmit={onApplicationSubmit} />
        }
      </div>
    </div>
  )
}

export default memo(DesignerInspector);
